import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { X, Sparkles, RotateCcw, ExternalLink } from 'lucide-react';
import { useContacts, type ContactFormData } from '@/hooks/useContacts';
import { useCompanies } from '@/hooks/useCompanies';
import type { BusinessCardScanResult } from '@/hooks/useBusinessCardScanner';
import { formatPhoneNumber } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ContactFormWithScannerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact?: any;
  onSubmit?: () => void;
  scannedData?: BusinessCardScanResult;
  onRescan?: () => void;
}

export function ContactFormWithScanner({
  open,
  onOpenChange,
  contact,
  onSubmit: onSubmitCallback,
  scannedData,
  onRescan,
}: ContactFormWithScannerProps) {
  const { createContact, updateContact } = useContacts();
  const { companies, createCompany } = useCompanies();
  const [loading, setLoading] = useState(false);
  const [newTag, setNewTag] = useState('');
  const [aiExtractedFields, setAiExtractedFields] = useState<Set<string>>(new Set());
  const [showCreateCompanyDialog, setShowCreateCompanyDialog] = useState(false);
  const [pendingCompanyName, setPendingCompanyName] = useState<string>('');
  const [isCreatingCompany, setIsCreatingCompany] = useState(false);

  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    company_id: '',
    job_title: '',
    department: '',
    is_primary_contact: false,
    is_decision_maker: false,
    linkedin_url: '',
    contact_type: 'lead',
    status: 'new',
    notes: '',
    tags: [],
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'USA'
  });

  // Populate form when dialog opens
  useEffect(() => {
    if (open) {
      if (scannedData) {
        // Pre-populate with scanned data
        const extracted = scannedData.extractedData;
        const fieldsSet = new Set<string>();
        
        // Try to match company by name
        let matchedCompanyId = '';
        if (extracted.company && companies) {
          const matchingCompany = companies.find(
            c => c.name.toLowerCase() === extracted.company?.toLowerCase()
          );
          if (matchingCompany) {
            matchedCompanyId = matchingCompany.id;
          }
        }
        
        setFormData({
          name: extracted.name || '',
          email: extracted.email || '',
          phone: extracted.phone || '',
          company_id: matchedCompanyId,
          job_title: extracted.job_title || '',
          department: extracted.department || '',
          is_primary_contact: false,
          is_decision_maker: false,
          linkedin_url: extracted.linkedin_url || '',
          contact_type: 'lead',
          status: 'new',
          notes: extracted.notes || '',
          tags: [],
          address_line1: extracted.address_line1 || '',
          address_line2: extracted.address_line2 || '',
          city: extracted.city || '',
          state: extracted.state || '',
          zip_code: extracted.zip_code || '',
          country: extracted.country || 'USA',
          business_card_image_path: scannedData.imagePath,
          business_card_image_url: scannedData.imageUrl,
          extraction_metadata: {
            confidence: extracted.confidence,
            rawExtraction: scannedData.rawExtraction,
          },
        });

        // Track which fields were AI-extracted
        if (extracted.name) fieldsSet.add('name');
        if (extracted.email) fieldsSet.add('email');
        if (extracted.phone) fieldsSet.add('phone');
        if (extracted.job_title) fieldsSet.add('job_title');
        if (extracted.department) fieldsSet.add('department');
        if (extracted.linkedin_url) fieldsSet.add('linkedin_url');
        if (extracted.notes) fieldsSet.add('notes');
        if (extracted.address_line1) fieldsSet.add('address_line1');
        if (extracted.address_line2) fieldsSet.add('address_line2');
        if (extracted.city) fieldsSet.add('city');
        if (extracted.state) fieldsSet.add('state');
        if (extracted.zip_code) fieldsSet.add('zip_code');
        if (extracted.country) fieldsSet.add('country');
        
        setAiExtractedFields(fieldsSet);
      } else if (contact) {
        // Edit existing contact
        setFormData({
          name: contact.name || '',
          email: contact.email || '',
          phone: contact.phone || '',
          company_id: contact.company_id || '',
          job_title: contact.job_title || '',
          department: contact.department || '',
          is_primary_contact: contact.is_primary_contact || false,
          is_decision_maker: contact.is_decision_maker || false,
          linkedin_url: contact.linkedin_url || '',
          contact_type: contact.contact_type || 'lead',
          status: contact.status || 'new',
          notes: contact.notes || '',
          tags: contact.tags || [],
          address_line1: contact.address_line1 || '',
          address_line2: contact.address_line2 || '',
          city: contact.city || '',
          state: contact.state || '',
          zip_code: contact.zip_code || '',
          country: contact.country || 'USA',
          business_card_image_path: contact.business_card_image_path,
          business_card_image_url: contact.business_card_image_url,
          extraction_metadata: contact.extraction_metadata,
        });
        setAiExtractedFields(new Set());
      } else {
        // Reset for new contact
        setFormData({
          name: '',
          email: '',
          phone: '',
          company_id: '',
          job_title: '',
          department: '',
          is_primary_contact: false,
          is_decision_maker: false,
          linkedin_url: '',
          contact_type: 'lead',
          status: 'new',
          notes: '',
          tags: [],
          address_line1: '',
          address_line2: '',
          city: '',
          state: '',
          zip_code: '',
          country: 'USA'
        });
        setAiExtractedFields(new Set());
      }
    }
  }, [open, contact, scannedData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Debug logging
    console.log('🔍 Submit check:', {
      scannedCompany: scannedData?.extractedData.company,
      formCompanyId: formData.company_id,
      companiesLoaded: !!companies,
      companiesCount: companies?.length
    });
    
    // Check if we need to prompt for company creation
    // If company_id is empty but we have a scanned company name, the company doesn't exist
    if (scannedData?.extractedData.company && !formData.company_id) {
      const scannedCompanyName = scannedData.extractedData.company.trim();
      console.log('🏢 Company does not exist, showing creation dialog:', scannedCompanyName);
      
      // Show dialog to confirm company creation
      setPendingCompanyName(scannedCompanyName);
      setShowCreateCompanyDialog(true);
      return; // Stop submission until user confirms
    }
    
    console.log('✅ Proceeding with contact creation');
    setLoading(true);
    try {
      if (contact) {
        await updateContact(contact.id, formData);
      } else {
        await createContact(formData);
      }
      
      onSubmitCallback?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving contact:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCompanyAndSubmit = async () => {
    setIsCreatingCompany(true);
    try {
      const newCompany = await createCompany({
        name: pendingCompanyName,
        company_type: 'prospect',
        status: 'active',
      });
      
      // Update form with new company
      setFormData(prev => ({ ...prev, company_id: newCompany.id }));
      setShowCreateCompanyDialog(false);
      
      // Now submit the contact with the new company
      setLoading(true);
      if (contact) {
        await updateContact(contact.id, { ...formData, company_id: newCompany.id });
      } else {
        await createContact({ ...formData, company_id: newCompany.id });
      }
      
      onSubmitCallback?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create company:', error);
    } finally {
      setIsCreatingCompany(false);
      setLoading(false);
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags?.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  const FieldLabel = ({ htmlFor, children, isAiExtracted }: { htmlFor: string; children: React.ReactNode; isAiExtracted: boolean }) => (
    <Label htmlFor={htmlFor} className="flex items-center gap-2">
      {children}
      {isAiExtracted && (
        <Badge variant="secondary" className="text-xs px-1.5 py-0">
          <Sparkles className="h-3 w-3 mr-1" />
          AI
        </Badge>
      )}
    </Label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {scannedData && <Sparkles className="h-5 w-5 text-primary" />}
            {contact ? 'Edit Contact' : scannedData ? 'Review Scanned Contact' : 'Add New Contact'}
          </DialogTitle>
          {scannedData && (
            <div className="flex items-center gap-2 pt-2">
              <p className="text-sm text-muted-foreground">
                Business card scanned with {Math.round((scannedData.extractedData.confidence || 0) * 100)}% confidence
              </p>
              {onRescan && (
                <Button variant="ghost" size="sm" onClick={onRescan}>
                  <RotateCcw className="h-4 w-4 mr-1" />
                  Rescan
                </Button>
              )}
            </div>
          )}
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {scannedData?.imageUrl && (
            <div className="rounded-lg border p-2 bg-muted">
              <img 
                src={scannedData.imageUrl} 
                alt="Business card" 
                className="w-full h-auto rounded"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <FieldLabel htmlFor="name" isAiExtracted={aiExtractedFields.has('name')}>
                Name *
              </FieldLabel>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>

            <div>
              <FieldLabel htmlFor="email" isAiExtracted={aiExtractedFields.has('email')}>
                Email
              </FieldLabel>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <FieldLabel htmlFor="phone" isAiExtracted={aiExtractedFields.has('phone')}>
                Phone
              </FieldLabel>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={formData.phone ? formatPhoneNumber(formData.phone) : ''}
              />
              {formData.phone && (
                <p className="text-xs text-muted-foreground mt-1">
                  Formatted: {formatPhoneNumber(formData.phone)}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="company" className="flex items-center gap-2">
                Company
                {scannedData?.extractedData.company && !formData.company_id && (
                  <Badge variant="outline" className="text-xs">
                    "{scannedData.extractedData.company}" not found
                  </Badge>
                )}
              </Label>
              <Select
                value={formData.company_id}
                onValueChange={(value) => setFormData({ ...formData, company_id: value })}
              >
                <SelectTrigger id="company">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies?.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {scannedData?.extractedData.company && !formData.company_id && (
                <p className="text-xs text-muted-foreground mt-1">
                  Create "{scannedData.extractedData.company}" in Companies first
                </p>
              )}
            </div>

            <div>
              <FieldLabel htmlFor="job_title" isAiExtracted={aiExtractedFields.has('job_title')}>
                Job Title
              </FieldLabel>
              <Input
                id="job_title"
                value={formData.job_title}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
              />
            </div>

            <div>
              <FieldLabel htmlFor="department" isAiExtracted={aiExtractedFields.has('department')}>
                Department
              </FieldLabel>
              <Input
                id="department"
                value={formData.department}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              />
            </div>

            <div>
              <FieldLabel htmlFor="linkedin_url" isAiExtracted={aiExtractedFields.has('linkedin_url')}>
                LinkedIn URL
              </FieldLabel>
              <Input
                id="linkedin_url"
                type="url"
                value={formData.linkedin_url}
                onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-4 pt-4 border-t">
            <h3 className="text-sm font-medium">Address</h3>
            
            <div className="col-span-2">
              <FieldLabel htmlFor="address_line1" isAiExtracted={aiExtractedFields.has('address_line1')}>
                Street Address
              </FieldLabel>
              <Input
                id="address_line1"
                value={formData.address_line1}
                onChange={(e) => setFormData({ ...formData, address_line1: e.target.value })}
                placeholder="123 Main Street"
              />
            </div>

            <div className="col-span-2">
              <FieldLabel htmlFor="address_line2" isAiExtracted={aiExtractedFields.has('address_line2')}>
                Apt, Suite, Unit, etc.
              </FieldLabel>
              <Input
                id="address_line2"
                value={formData.address_line2}
                onChange={(e) => setFormData({ ...formData, address_line2: e.target.value })}
                placeholder="Apt 4B"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <FieldLabel htmlFor="city" isAiExtracted={aiExtractedFields.has('city')}>
                  City
                </FieldLabel>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="New York"
                />
              </div>

              <div>
                <FieldLabel htmlFor="state" isAiExtracted={aiExtractedFields.has('state')}>
                  State
                </FieldLabel>
                <Input
                  id="state"
                  value={formData.state}
                  onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  placeholder="NY"
                />
              </div>

              <div>
                <FieldLabel htmlFor="zip_code" isAiExtracted={aiExtractedFields.has('zip_code')}>
                  Zip Code
                </FieldLabel>
                <Input
                  id="zip_code"
                  value={formData.zip_code}
                  onChange={(e) => setFormData({ ...formData, zip_code: e.target.value })}
                  placeholder="10001"
                />
              </div>
            </div>

            <div>
              <FieldLabel htmlFor="country" isAiExtracted={aiExtractedFields.has('country')}>
                Country
              </FieldLabel>
              <Input
                id="country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                placeholder="USA"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact_type">Contact Type *</Label>
              <Select
                value={formData.contact_type}
                onValueChange={(value: any) => setFormData({ ...formData, contact_type: value })}
              >
                <SelectTrigger id="contact_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="vendor">Vendor</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value: any) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger id="status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center space-x-6">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="primary_contact"
                checked={formData.is_primary_contact}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_primary_contact: checked as boolean })
                }
              />
              <Label htmlFor="primary_contact" className="font-normal cursor-pointer">
                Primary Contact
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="decision_maker"
                checked={formData.is_decision_maker}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_decision_maker: checked as boolean })
                }
              />
              <Label htmlFor="decision_maker" className="font-normal cursor-pointer">
                Decision Maker
              </Label>
            </div>
          </div>

          <div>
            <FieldLabel htmlFor="notes" isAiExtracted={aiExtractedFields.has('notes')}>
              Notes
            </FieldLabel>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="tags">Tags</Label>
            <div className="flex gap-2 mb-2">
              <Input
                id="tags"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add a tag and press Enter"
              />
              <Button type="button" onClick={addTag} variant="outline">
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.tags?.map((tag) => (
                <Badge key={tag} variant="secondary" className="gap-1">
                  {tag}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => removeTag(tag)}
                  />
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? 'Saving...' : contact ? 'Update Contact' : 'Add Contact'}
            </Button>
          </div>
        </form>
      </DialogContent>

      <AlertDialog open={showCreateCompanyDialog} onOpenChange={setShowCreateCompanyDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create Company?</AlertDialogTitle>
            <AlertDialogDescription>
              Company "{pendingCompanyName}" doesn't exist in your database. Would you like to create it?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isCreatingCompany}>
              No, I'll select manually
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCreateCompanyAndSubmit}
              disabled={isCreatingCompany}
            >
              {isCreatingCompany ? 'Creating...' : 'Yes, create it'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
